/**
 * Shared classification for `sandbox_provision_timings` rows.
 *
 * Both the API (which decides what a non-superadmin is allowed to see) and the
 * web chart (which buckets bars into Ready / Build) need the same answer to
 * "which half of the provision does this step belong to". Keeping the rule here
 * means a new step added to the provision job cannot land in one place and be
 * missing from the other.
 *
 * @module provision-timings
 */

/**
 * The two halves a provision is presented as: getting the sandbox **ready**
 * (booted, repo on the target branch — the sub-30s goal) and **building** it
 * (images built, app answering healthy — the deploy floor).
 */
export type ProvisionTimingGroup = 'ready' | 'build';

/**
 * Steps that happen after the app has started building. Everything not listed
 * gets the sandbox ready.
 *
 * `phase7_health` and the `health_stage` rows land in Build because waiting for
 * the app to answer is part of building it, not part of having a machine.
 */
const BUILD_STEPS: ReadonlySet<string> = new Set([
  'app_up',
  'startup',
  'seed',
  'phase6_build',
  'phase7_health',
  'base_seeds',
  'capture_artifacts',
]);

/**
 * Phase rows that are *containers* for finer rows rather than work of their own:
 * `total` spans the run, `phase4_deploy`'s breakdown is the `deploy_step` rows,
 * and `stopped_pool_claim` sits inside `phase1_allocate`. Summing or charting
 * one of these alongside its children counts the same seconds twice.
 */
export const NON_LEAF_PHASE_STEPS: ReadonlySet<string> = new Set([
  'total',
  'phase4_deploy',
  'stopped_pool_claim',
]);

/**
 * Which half of the provision a timing row belongs to.
 *
 * The `cache_restore` branch is a guard, not a live path: those rows are
 * per-service children charted under their parent step, and neither consumer
 * feeds them here today. It exists because their `step` is a customer's compose
 * service name — a service named `startup` or `app_up` would otherwise collide
 * with BUILD_STEPS and be classified by coincidence. They belong to Ready with
 * their parent: restoring the cache is part of getting the sandbox up.
 */
export function provisionTimingGroupOf(kind: string, step: string): ProvisionTimingGroup {
  // Container builds (deps, web-build) are the build by definition, and health
  // stages follow their parent gate.
  if (kind === 'container' || kind === 'health_stage') return 'build';
  if (kind === 'cache_restore') return 'ready';
  return BUILD_STEPS.has(step) ? 'build' : 'ready';
}

/**
 * The reduced payload a non-superadmin receives: one duration per half plus the
 * whole-provision total, and nothing else. Deliberately not a `ProvisionTiming`
 * row shape — a customer is shown a summary, not a filtered timeline, so the
 * type makes the two views structurally distinct instead of hoping a filter was
 * applied.
 *
 * Carries one bit about the launch — `warmStart` — because a customer comparing
 * a 95s launch against a 300s one needs to know which one they got. *Which*
 * warm path served them (`warm_cold`) and how the deploy avoided a rebuild
 * (`strategy`) are our mechanics and stay on the superadmin row payload.
 *
 * `warmStart` is `null` for runs recorded before the launch source existed —
 * unknown, which the chart renders as no badge rather than as "cold".
 */
export type ProvisionTimingSummary = {
  totalMs: number;
  readyMs: number;
  buildMs: number;
  warmStart: boolean | null;
};
